import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { supabase } from '../supabaseClient';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardbackground from '../cardbackground.webp';
import { checkReadingLimit, handleDeleteReading } from '../utils/readingUtils';

const SelfCare = ({ goBack, goHome }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCards, setSelectedCards] = useState([]);
    const [savedReadings, setSavedReadings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [isLoadingPickedCards, setIsLoadingPickedCards] = useState(false);
    const [shuffledCards, setShuffledCards] = useState([]);

    const retrySupabaseQuery = async (queryFn, retries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    console.warn(`Retrying query (attempt ${attempt})...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    };

    const isDisplayOnlyCard = (card) => {
        const imagePath = card?.image_path?.toLowerCase() || '';
        return imagePath.includes('scback094');
    };

    const fetchCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions')
                    .eq('component', 'SelfCare')
            );
            if (error) throw error;
            if (data && data.length > 0) {
                const selectableCards = data.filter((card) => !isDisplayOnlyCard(card));
                console.log('Fetched cards:', selectableCards); // Debug log
                setCards(selectableCards);
                setShuffledCards(shuffleArray([...selectableCards]));
                console.log('Shuffled cards length:', selectableCards.length); // Debug length
            } else {
                throw new Error('No cards fetched');
            }
        } catch (err) {
            toast.error('Failed to load cards: ' + err.message);
            console.error('Error fetching cards:', err.message);
            setCards([]);
            setShuffledCards([]);
        } finally {
            setLoading(false); // Ensure loading clears
        }
    }, []);

    const fetchSavedReadings = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('component', 'SelfCare')
                    .order('created_at', { ascending: false })
            );
            if (error) throw error;
            setSavedReadings(data || []);
        } catch (err) {
            console.error('Error fetching saved readings:', err.message);
        }
    }, []);

    useEffect(() => {
        fetchCards();
        fetchSavedReadings();
    }, [fetchCards, fetchSavedReadings]);

    useEffect(() => {
        if (selectedCards.length > 0) {
            setIsLoadingPickedCards(true);
            const timer = setTimeout(() => setIsLoadingPickedCards(false), 500);
            return () => clearTimeout(timer);
        }
    }, [selectedCards]);

    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const handleCardClick = (card) => {
        if (selectedCards.length < 3 && !selectedCards.includes(card)) {
            setSelectedCards([...selectedCards, card]);
        }
    };

    const handleSaveReading = async () => {
        if (selectedCards.length !== 3) {
            toast.error('Please select exactly 3 cards.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                toast.error('User not logged in.');
                setSaving(false); // Reset on auth failure
                return;
            }

            const canSave = await checkReadingLimit(user.id, 'SelfCare', retrySupabaseQuery, supabase);
            if (!canSave) {
                toast.error('You have reached the limit of 10 saved readings for this picker. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = selectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.SelfCare?.[position] || '';
                return acc;
            }, {});

            const { error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert({
                        user_id: user.id,
                        component: 'SelfCare',
                        cards: selectedCards.map((card) => card.id),
                        descriptions,
                    })
            );

            if (error) {
                if (error.status === 404) {
                    toast.error('Failed to save reading: endpoint not found.');
                } else {
                    throw error;
                }
            } else {
                toast.success('Reading saved successfully!');
                setSelectedCards([]);
                setPickerVisible(false);
                await fetchSavedReadings();
            }
        } catch (err) {
            if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
                toast.error('Network error: Please check your connection and try again.');
            } else {
                toast.error('Failed to save reading.');
            }
            console.error('Error saving reading:', err.message);
        } finally {
            setSaving(false); // Ensure reset on failure
        }
    };

    const handleLoadReading = (reading) => {
        // Prevent loading a saved reading before cards are fetched
        if (loading || cards.length === 0) {
            toast.info('Loading card data. Please wait a moment and try again.');
            return;
        }

        const cardsById = cards.reduce((acc, card) => {
            acc[card.id] = card;
            return acc;
        }, {});

        const loadedCards = reading.cards.map((cardId) => cardsById[cardId]).filter(Boolean);

        if (loadedCards.length !== (reading.cards || []).length) {
            toast.error('Some cards could not be loaded for this saved reading. Please refresh and try again.');
        }

        setSelectedCards(loadedCards);
        setPickerVisible(false);
    };

    const handleSaveProgress = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in.');
                return;
            }

            const { error: progressError } = await supabase
                .from('user_progress', { schema: 'api' })
                .upsert({
                    user_id: user.id,
                    current_component: 'SelfCare',
                    animated_helper: 'no', // always set to 'no'
                    timestamp: new Date().toISOString(),
                });

            if (progressError) {
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            alert('Session progress has been saved successfully!');
        } catch (err) {
            alert('Failed to save progress.');
            console.error('Error saving progress:', err.message);
        }
    };

    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible(!pickerVisible);
        if (cards.length > 0) {
            setShuffledCards(shuffleArray([...cards]));
            console.log('Shuffled cards set:', shuffledCards); // Debug log
        }
    };

    return (
        <Container>
            <h2
                style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '36px',
                    fontWeight: 'bold',
                    color: '#4B5563',
                    textAlign: 'center',
                    marginTop: '16px',
                }}
            >
                Self Care
            </h2>

            {/* Session Helper Cards Row */}
            <div
                className="selfcare-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardbackground})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• Self care is our physical body care and our emotional and overall care</div>
                        <div>• Time in Nature is important for Self Care</div>
                        <div>• Good Nutrition is important for Self Care</div>
                        <div>• Putting ourselves in the equation for our plans and experiences is important</div>
                        <div>• Consistent self care habits can be established at any time with repetition</div>
                        <div>• The Self Care Oracle has Suggested self care items for right now</div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardbackground})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. Is your self care ritual well established?</div>
                        <div>2. Is there any way you would like to improve your self care?</div>
                        <div>3. What do you need to feel cared for right now?</div>
                        <div>4. Are there self care things you tend to procrastinate on?</div>
                        <div>5. Is there anything you can remove from your habits to make yourself feel more cared for?</div>
                        <div>6. Do you hold your own self care with as much importance as you do for those you care for?</div>
                    </div>
                </div>
            </div>

            <StackedCards>
                <Card style={{ backgroundImage: `url(${cardbackground})` }}>
                    <p
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: '16px',
                        }}
                    >
                       Our Self Care habits are formed in childhood but like with any habit we can establish changes at any time.  <br /> There are a lot of coping mechanisms that can interfere with prioritizing good self care. If you know your self care is lacking then you may be deprogramming from coping patterns that originated in your childhood. Your self care can also be lacking when you get run down in high-stress situations for long periods of time. Not everyone has this issue to contend with, but when self care has not been prioritized we will want to do what it takes to include it in our daily habits.  
                    </p>
                    <p
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: '16px',
                        }}
                    >
                        Showing up for ourselves with our self care every day over time adds up to high self-esteem. If we put the effort in or if we don't put the effort in, the time will pass anyway.
                    </p>
                    <p
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: '16px',
                        }}
                    >
                        Sometimes we are burdened with responsibilities and the only place we have any room is to forgo some of the self care we would otherwise do. It is important to just do our best. One step at a time and celebrate every time we show up for ourselves. Our time and energy is our resource and it needs to be allocated wisely. 
                    </p>
                    <p
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: '16px',
                        }}
                    >
                        Asses the resources you have and make sure you prioritize giving yourself what you need and only give from your overflow.  Remove yourself from draining or unsafe situations. Ask for help when needed. 
                    </p>
                </Card>
                <Card style={{ backgroundImage: `url(${cardbackground})` }}>
                    <p
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: '16px',
                        }}
                    >
                        Self Care is not only about how we physically care for our body, though that is a big part of it.  We also need to care about ourselves in general. We care about our well-being.  We also care about the feelings we have and the consequences we create with the decisions we make.  All of it matters to us.  We can find ourselves just going through the motions.  What if we slow down and ask ourselves what we need to feel cared for?  
                    </p>
                </Card>
                <Card style={{ backgroundImage: `url(${cardbackground})` }}>
                    <p
                        style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            lineHeight: '1.5',
                            margin: '16px',
                        }}
                    >
                        Sometimes it can help to have a little prompt or some guidance about what will be most helpful in terms of self care. I have included a Card Picker for you on this page. Start Reading and use your intuition to choose three cards. Your Reading will be displayed below. Save it if you want to be able to revisit it again in the future.

                    </p>
                </Card>
            </StackedCards>
            {loading ? (
                <SpinnerContainer>
                    <ClipLoader size={50} color="#4B5563" />
                </SpinnerContainer>
            ) : (
                <>
                    <CenteredButton onClick={handleStartReading}>
                        {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
                    </CenteredButton>
                    {pickerVisible && (
    <>
        {window.innerWidth > 600 ? (
            <CardPicker>
                {Array.from({ length: Math.ceil(shuffledCards.length / 25) }).map((_, rowIndex) => {
                    const start = rowIndex * 25;
                    const end = Math.min(start + 25, shuffledCards.length);
                    return (
                        <div
                            key={rowIndex}
                            style={{
                                display: 'flex',
                                flex: '0 0 100%',
                                marginTop: rowIndex === 0 ? 0 : '-20px',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            {shuffledCards.slice(start, end).map((card, index) => (
                                <PickerCard
                                    key={card.id}
                                    index={index}
                                    whileTap={{ scale: 1.1 }}
                                    onClick={() => handleCardClick(card)}
                                    aria-label={`Card ${start + index + 1}`}
                                    tabIndex={0}
                                >
                                    <img
                                        src="https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/scback094.webp"
                                        alt="Card Back"
                                        loading="lazy"
                                        onError={() => console.error('Failed to load card back image: scback094.webp')}
                                    />
                                </PickerCard>
                            ))}
                        </div>
                    );
                })}
            </CardPicker>
        ) : (
            <Card style={{ backgroundColor: 'white', padding: '16px', borderRadius: '10px', position: 'relative', minHeight: '400px' }}>
                <CardPicker>
                    {Array.from({ length: 3 }).map((_, rowIndex) => {
                        const start = rowIndex * 25;
                        const end = Math.min(rowIndex === 2 ? start + 26 : start + 25, shuffledCards.length);
                        if (start >= shuffledCards.length) return null;
                        return (
                            <div
                                key={rowIndex}
                                style={{
                                    position: 'relative',
                                    height: '130px',
                                    marginTop: rowIndex === 0 ? 0 : '10px',
                                    overflow: 'hidden',
                                }}
                            >
                                {shuffledCards.slice(start, end).map((card, index) => (
                                    <PickerCard
                                        key={card.id}
                                        index={index + start}
                                        whileTap={{ scale: 1.1 }}
                                        onClick={() => handleCardClick(card)}
                                        aria-label={`Card ${start + index + 1}`}
                                        tabIndex={0}
                                        style={{
                                            position: 'absolute',
                                            top: '0',
                                            left: `${index * 10}px`,
                                            marginLeft: index === 25 && rowIndex === 2 ? '0' : '-70px',
                                            zIndex: index % 26,
                                        }}
                                    >
                                        <img
                                            src="https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/scback094.webp"
                                            alt="Card Back"
                                            loading="lazy"
                                            onError={() => console.error('Failed to load card back image: scback094.webp')}
                                        />
                                    </PickerCard>
                                ))}
                            </div>
                        );
                    })}
                    {shuffledCards.length === 0 && <p>No cards available. Please check your data source.</p>}
                </CardPicker>
            </Card>
        )}
    </>
)}
                    {selectedCards.length === 3 && (
                        isLoadingPickedCards ? (
                            <SpinnerContainer>
                                <ClipLoader size={50} color="#4B5563" />
                            </SpinnerContainer>
                        ) : (
                            <>
                                <GridCard>
                                    {selectedCards.map((card, index) => (
                                        <SelectedCard
                                            key={card.id}
                                            as={motion.div}
                                            initial={{ y: -50, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ delay: index * 0.2 }}
                                        >
                                            <img
                                                src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                                alt={card.title}
                                                onError={() => console.error(`Failed to load card image: ${card.image_path}`)}
                                            />
                                            <p>{['Doing Well', 'Add This', 'Less'][index]}</p>
                                        </SelectedCard>
                                    ))}
                                </GridCard>
                                <DescriptionCard>
                                    {selectedCards.map((card, index) => (
                                        <p key={card.id}>
                                            <strong>{['Doing Well', 'Add This', 'Less'][index]}:</strong>{' '}
                                            {card.descriptions?.SelfCare?.[`position${index + 1}`] || 'No description available.'}
                                        </p>
                                    ))}
                                </DescriptionCard>
                                <CenteredButton onClick={handleSaveReading} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Reading'}
                                </CenteredButton>
                            </>
                        )
                    )}
                    <ButtonContainer>
                        <Button onClick={goBack}>Back</Button>
                        <Button onClick={goHome}>Home</Button>
                        <Button onClick={handleSaveProgress}>Save</Button>
                    </ButtonContainer>
                    <SavedReadings>
                        <h3>Saved Readings</h3>
                        {savedReadings.map((reading) => (
                            <SavedReading
                                key={reading.id}
                                tabIndex={0}
                                aria-label={`Saved reading from ${new Date(reading.created_at).toLocaleString()}`}
                            >
                                <span onClick={() => handleLoadReading(reading)}>
                                    {new Date(reading.created_at).toLocaleString()}
                                </span>
                                <DeleteLink
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteReading(reading.id, fetchSavedReadings, retrySupabaseQuery, supabase);
                                    }}
                                >
                                    -delete
                                </DeleteLink>
                            </SavedReading>
                        ))}
                    </SavedReadings>
                </>
            )}
        </Container>
    );
};

export default SelfCare; // Last line for consistency

// Styled Components
const Container = styled.div`
    background-image: url(${backgroundImage});
    background-size: cover;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
`;

const StackedCards = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
`;

const Card = styled.div`
    width: 100%;
    max-width: 600px;
    height: auto;
    background-size: cover;
    background-position: center;
    border: 2px solid black;
    border-radius: 10px;
    box-shadow: 3px 3px 5px rgba(0,0,0,0.3);
    padding: 20px;
    background-color: #f0f0f0;
    margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 600px) {
        flex-wrap: wrap;
        justify-content: center;
    }
`;

const Button = styled.button`
    background-image: url(${buttonImage});
    background-size: cover;
    border: 2px solid black;
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    fontWeight: bold;
    box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @media (max-width: 600px) {
        width: 90px;
        margin: 4px;
    }
`;

const CenteredButton = styled(Button)`
    margin: 16px auto;
    width: auto;
    padding: 10px 30px;

    @media (max-width: 600px) {
        width: auto;
        padding: 10px 40px;
    }
`;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const CardPicker = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    margin: 0 auto;
    padding: 8px;
    overflow-x: hidden;

    @media (max-width: 600px) {
        display: block;
    }
`;

const PickerCard = styled(motion.div)`
    width: 100px;
    height: 150px;
    cursor: pointer;
    margin-left: -50px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2), 0 -2px 4px rgba(0, 0, 0, 0.1);
    z-index: ${props => props.index % 25};
    &:first-child {
        margin-left: 0;
    }
    &:last-child {
        margin-left: -50px;
        flex-shrink: 0;
    }
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
    }

    @media (max-width: 600px) {
        width: 80px;
        height: 120px;
    }
`;

const GridCard = styled.div`
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 16px;
    background: white;
    border: 2px solid black;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 16px;
    border-radius: 8px;
`;

const SelectedCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    img {
        width: 160px;
        height: 80px;
        object-fit: cover;
    }
    p {
        margin-top: 8px;
        font-family: 'Times New Roman', Times, serif;
        font-size: 14px;
        fontWeight: 'bold';
    }

    @media (max-width: 600px) {
        img {
            width: 80px;
            height: 40px;
        }
    }
`;

const DescriptionCard = styled.div`
    margin-top: 16px;
    background: white;
    border: 2px solid black;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 16px;
    border-radius: 8px;
    text-align: center;

    p {
        font-family: 'Times New Roman', Times, serif';
        font-size: 14px;
        margin: 8px 0;
    }
`;

const SavedReadings = styled.div`
    margin-top: 32px;
    width: 100%;
    max-width: 600px;
    h3 {
        font-family: 'Times New Roman', Times, serif;
        fontSize: 18px;
        fontWeight: bold;
        margin-bottom: 16px;
    }
`;

const SavedReading = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
    border: 1px solid black;
    border-radius: 8px;
    margin-bottom: 8px;
    text-align: center;
    font-family: 'Times New Roman', Times, serif;
    font-size: 14px;
    &:hover {
        background-color: #f0f0f0;
    }
`;

const DeleteLink = styled.span`
    margin-left: 8px;
    color: red;
    cursor: pointer;
    font-size: 12px;
    &:hover {
        text-decoration: underline;
    }
`;